import { useAgentsRealtime } from '@/composables/realtime/useAgentsRealtime'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import type { VaLanguage, CallStatus, VirtualAgentSummary } from 'shared/src'
import { AgentStatus } from 'shared/src'
import type { ComposerTranslation } from 'vue-i18n'

import { useLanguages } from '@/composables/translations/useLanguages.ts'
import { isNotBlank } from '@/utils/String'

interface SelectedState {
  agents: string[]
  filters: {
    languages: VaLanguage[]
    statuses: CallStatus[]
    agentsStatuses: AgentStatus[]
  }
}
const currentAgentIndex = ref(0)

export const useLiveAgents = (locale: string, t?: ComposerTranslation) => {
  const translate: ComposerTranslation = t ?? ((key: string) => key) // fallback

  const { languageLabel } = useLanguages()
  const { agents, availableFilters, applyFilters } = useAgentsRealtime()

  const agentsList = computed(() => Object.values(agents.value))

  const selected = ref<SelectedState>({
    agents: [],
    filters: {
      languages: [],
      statuses: [],
      agentsStatuses: [],
    },
  })

  const initializedFilters = ref(false)

  onMounted(() => {
    const storedValue = localStorage.getItem(STORAGE_KEYS.LIVE_VIRTUAL_AGENTS)
    const saved = JSON.parse(isNotBlank(storedValue) ? storedValue : '{}') as Partial<SelectedState>

    selected.value.filters = {
      languages: saved.filters?.languages ?? [],
      statuses: saved.filters?.statuses ?? [],
      agentsStatuses: saved.filters?.agentsStatuses ?? [],
    }
    selected.value.agents = saved.agents ?? []

    // Trigger initial load with saved filters (or empty if none saved)
    applyFilters({
      ...selected.value.filters,
      agents: selected.value.agents,
    })
  })

  watch(
    selected,
    (value) => {
      // Only apply filters after initialization is complete
      if (!initializedFilters.value) return

      localStorage.setItem(STORAGE_KEYS.LIVE_VIRTUAL_AGENTS, JSON.stringify(value))
      applyFilters({
        ...value.filters,
        agents: value.agents,
      })
    },
    { deep: true },
  )

  watch(
    () => availableFilters.value,
    (filters) => {
      if (
        !initializedFilters.value &&
        filters.languages.length &&
        filters.statuses.length &&
        filters.agents.length &&
        filters.agentsStatuses.length
      ) {
        const savedRaw = localStorage.getItem(STORAGE_KEYS.LIVE_VIRTUAL_AGENTS)
        const saved = savedRaw ? (JSON.parse(savedRaw) as Partial<SelectedState>) : null
        const hasStorage = savedRaw !== null

        const normalized = {
          agents: saved?.agents ?? [],
          filters: {
            languages: saved?.filters?.languages ?? [],
            statuses: saved?.filters?.statuses ?? [],
            agentsStatuses: saved?.filters?.agentsStatuses ?? [],
          },
        }

        if (hasStorage) {
          selected.value.filters.languages = [...normalized.filters.languages]
          selected.value.filters.statuses = [...normalized.filters.statuses]
          selected.value.filters.agentsStatuses = [...normalized.filters.agentsStatuses]
          selected.value.agents = [...normalized.agents]
        } else {
          selected.value.filters.languages = []
          selected.value.filters.statuses = []
          selected.value.filters.agentsStatuses = []
          selected.value.agents = []
        }

        applyFilters({
          ...selected.value.filters,
          agents: selected.value.agents,
        })

        initializedFilters.value = true
      }
    },
    { deep: true },
  )

  const onOrderSaved = (newOrder: (string | number)[]) => {
    selected.value.agents = newOrder as string[]

    applyFilters({
      ...selected.value.filters,
      agents: selected.value.agents,
    })
  }

  const hasActiveCalls = (agent: (typeof agentsList.value)[number]) =>
    (agent.activeCallsCount || agent.activeCalls.length || 0) > 0

  const agentsToReorder = computed(() => {
    const selectedIds =
      selected.value.agents.length > 0 ? selected.value.agents : agentsList.value.map((a) => a.id)

    return agentsList.value
      .filter((agent) => selectedIds.includes(agent.id) && hasActiveCalls(agent))
      .map((agent) => ({
        id: agent.id,
        content: agent.name,
      }))
  })

  const virtualAgentsFiltersOptions = computed(() => ({
    languages: availableFilters.value.languages.map((lang: VaLanguage) => ({
      value: lang,
      label: languageLabel(locale, lang),
    })),
    statuses: availableFilters.value.statuses.map((status: CallStatus) => ({
      value: status,
      label: translate(status),
    })),
    agentsStatuses: availableFilters.value.agentsStatuses.map((status: AgentStatus) => {
      let label: string
      if (status === AgentStatus.Stable) {
        label = translate('stable')
      } else {
        label = translate('warning')
      }
      return {
        value: status,
        label,
      }
    }),
    agents: availableFilters.value.agents.map((va: VirtualAgentSummary) => ({
      value: va.id,
      label: va.name,
    })),
  }))

  const isAllSelected = computed(() => {
    const allIds = availableFilters.value.agents.map((a) => a.id)
    return allIds.length > 0 && allIds.every((id) => selected.value.agents.includes(id))
  })

  const isAllLanguagesSelected = computed(() => {
    const allLangs = availableFilters.value.languages
    return (
      allLangs.length > 0 &&
      allLangs.every((lang) => selected.value.filters.languages.includes(lang))
    )
  })

  const isAllStatusesSelected = computed(() => {
    const allStatuses = availableFilters.value.statuses
    return (
      allStatuses.length > 0 &&
      allStatuses.every((status) => selected.value.filters.statuses.includes(status))
    )
  })

  const isAllAgentsStatusesSelected = computed(() => {
    const allAgentStatuses = availableFilters.value.agentsStatuses
    return (
      allAgentStatuses.length > 0 &&
      allAgentStatuses.every((s) => selected.value.filters.agentsStatuses.includes(s))
    )
  })

  const hasActiveFilters = computed(() => {
    // Don't show reset button until filters are initialized
    if (!initializedFilters.value) return false

    return (
      selected.value.agents.length > 0 ||
      selected.value.filters.languages.length > 0 ||
      selected.value.filters.statuses.length > 0 ||
      selected.value.filters.agentsStatuses.length > 0
    )
  })

  const resetFilters = () => {
    selected.value.agents = []
    selected.value.filters.languages = []
    selected.value.filters.statuses = []
    selected.value.filters.agentsStatuses = []

    applyFilters({
      ...selected.value.filters,
      agents: selected.value.agents,
    })
  }

  const currentAgent = computed(() => agentsList.value[currentAgentIndex.value])
  function findNextAgentIndex(fromIndex: number) {
    for (let i = fromIndex + 1; i < agentsList.value.length; i++) {
      if (agentsList.value[i].activeCalls.length > 0) return i
    }
    return fromIndex
  }

  function findPrevAgentIndex(fromIndex: number) {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (agentsList.value[i].activeCalls.length > 0) return i
    }
    return fromIndex
  }
  const nextAgent = () => {
    const next = findNextAgentIndex(currentAgentIndex.value)
    if (next !== currentAgentIndex.value) currentAgentIndex.value = next
  }

  const prevAgent = () => {
    const prev = findPrevAgentIndex(currentAgentIndex.value)
    if (prev !== currentAgentIndex.value) currentAgentIndex.value = prev
  }

  return {
    // state
    agentsList,
    selected,

    // computed
    agentsToReorder,
    virtualAgentsFiltersOptions,
    isAllSelected,
    isAllLanguagesSelected,
    isAllStatusesSelected,
    isAllAgentsStatusesSelected,
    hasActiveFilters,
    currentAgent,

    // navigation
    currentAgentIndex,
    nextAgent,
    prevAgent,

    // methods
    onOrderSaved,
    resetFilters,
  }
}
